// AIBOX-03 模型參數（plan/SPEC.md vms-web AIBOX-03）：區域多邊形、越線、靈敏度、排程。座標是畫面的正規化值 0..1。

export type Pt = [number, number];

export interface ModelSchedule {
  days: number[]; // 0 = 週日
  start: string; // HH:MM
  end: string;
}

export interface ModelParams {
  sensitivity?: number;
  zone?: Pt[];
  line?: [Pt, Pt];
  direction?: 'both' | 'a_to_b' | 'b_to_a';
  schedule?: ModelSchedule;
  [k: string]: unknown;
}

export const ZONE_MIN = 3;
export const ZONE_MAX = 16;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const round = (v: number) => Math.round(clamp01(v) * 10_000) / 10_000;

/** 畫面上的點（相對元素左上、像素）→ 正規化座標。 */
export function toNorm(x: number, y: number, w: number, h: number): Pt {
  return [round(w > 0 ? x / w : 0), round(h > 0 ? y / h : 0)];
}

/** 點到最近的頂點（正規化距離 < tol）；沒有回 -1。拖曳頂點用。 */
export function nearestVertex(pts: Pt[], p: Pt, tol = 0.03): number {
  let best = -1;
  let bd = tol;
  pts.forEach((q, i) => {
    const d = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
}

/** 這個模型用不用越線 / 區域（依模型 id 的名稱；不認得的只給靈敏度與排程）。 */
export function modelUses(model: string): { zone: boolean; line: boolean } {
  const name = model.split('@')[0];
  if (name.endsWith('line_cross')) return { zone: false, line: true };
  return { zone: true, line: false };
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** 檢查：回問題代碼（給 i18n `aiset.err_<code>`）；沒問題回 null。 */
export function checkParams(model: string, p: ModelParams): string | null {
  const uses = modelUses(model);
  if (p.sensitivity !== undefined && (!Number.isInteger(p.sensitivity) || p.sensitivity < 10 || p.sensitivity > 100)) return 'sensitivity';
  if (p.zone && (p.zone.length < ZONE_MIN || p.zone.length > ZONE_MAX)) return 'zone_points';
  if (uses.line && !p.line) return 'line_required';
  if (p.line && Math.hypot(p.line[0][0] - p.line[1][0], p.line[0][1] - p.line[1][1]) < 0.02) return 'line_short';
  if (p.schedule) {
    if (!p.schedule.days.length || p.schedule.days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return 'schedule_days';
    if (!HHMM.test(p.schedule.start) || !HHMM.test(p.schedule.end) || p.schedule.start === p.schedule.end) return 'schedule_time';
  }
  return null;
}

/** 送出前整理：沒用到的欄位拿掉（這個模型不用的區域 / 線、空排程）。其他不認得的鍵原樣保留。 */
export function cleanParams(model: string, p: ModelParams): ModelParams {
  const uses = modelUses(model);
  const out: ModelParams = { ...p };
  if (!uses.zone || !out.zone?.length) delete out.zone;
  if (!uses.line || !out.line) {
    delete out.line;
    delete out.direction;
  } else out.direction = out.direction ?? 'both';
  if (!out.schedule) delete out.schedule;
  else out.schedule = { ...out.schedule, days: [...new Set(out.schedule.days)].sort((a, b) => a - b) };
  return out;
}
